/** @jsxImportSource @emotion/react */
import { Fragment } from "react";

/**
 * Renderiza un texto configurable con acentos, escrito con etiquetas entre
 * corchetes.
 *
 *   [b]negrita[/b]   [i]cursiva[/i]   [r]rojo[/r]
 *   [url=https://...]texto del link[/url]
 *   [u]subrayado[/u]
 *   [li]ítem con viñeta[/li]
 *   [ind]texto indentado[/ind]
 *
 * [li] y [ind] son bloques (ocupan su propia línea) y se pueden combinar:
 * [ind][li]ítem indentado[/li][/ind]. Cada uno debe abrirse y cerrarse dentro
 * del mismo párrafo (una línea en blanco corta el párrafo).
 *
 * Se anidan: [r][b]rojo y negrita[/b][/r].
 * Una línea en blanco separa párrafos.
 *
 * POR QUÉ CORCHETES Y NO HTML: este texto lo escribe un administrador y lo lee
 * un cliente. Renderizar HTML de un campo editable obliga a
 * dangerouslySetInnerHTML, que convierte a ese campo en un vector de XSS
 * —bastaría con que alguien entrara al panel para inyectar un script en una
 * pantalla ajena—. Acá el parseo es propio y produce elementos React, así que
 * nunca llega markup arbitrario al DOM: lo que no sea una de estas etiquetas se
 * muestra como texto literal.
 */

// Etiquetas soportadas y qué elemento produce cada una.
const ETIQUETAS = {
  b: (hijos, i) => <strong key={i}>{hijos}</strong>,
  i: (hijos, i) => <em key={i}>{hijos}</em>,
  r: (hijos, i) => (
    <span key={i} css={{ color: "var(--rojo)" }}>
      {hijos}
    </span>
  ),
  u: (hijos, i) => <u key={i}>{hijos}</u>,
  li: (hijos, i) => (
    <span
      key={i}
      css={{
        display: "block",
        paddingLeft: "1.25rem",
        textIndent: "-1.25rem",
        "&::before": { content: '"•"', display: "inline-block", width: "1.25rem", textIndent: 0 }
      }}
    >
      {hijos}
    </span>
  ),
  ind: (hijos, i) => (
    <span key={i} css={{ display: "block", paddingLeft: "2rem" }}>
      {hijos}
    </span>
  )
};

// Abre una etiqueta simple ([b], [i], [r]), un link ([url=...]) o cierra una.
const TOKEN = /\[(\/)?(b|i|r|u|li|ind|url)(?:=([^\]]*))?\]/g;

// El salto de línea que sigue al cierre de un bloque ya lo da el bloque: sin
// quitarlo, "pre-line" dejaría una línea en blanco de más entre ítems.
const SALTO_TRAS_BLOQUE = /(\[\/(?:li|ind)\])[ \t]*\n/g;

/**
 * Convierte un párrafo en nodos React.
 *
 * Recorre el texto una sola vez llevando una pila de etiquetas abiertas. Un
 * cierre sin apertura, o una etiqueta que queda sin cerrar, no rompen nada: el
 * texto se muestra igual. Un campo de configuración mal escrito tiene que
 * mostrarse feo, no dejar la pantalla en blanco.
 */
function nodosDeParrafo(texto) {
  // Cada nivel de la pila junta los hijos de una etiqueta abierta.
  const pila = [{ etiqueta: null, url: null, hijos: [] }];
  let indice = 0;
  let clave = 0;

  const agregarTexto = (crudo) => {
    if (!crudo) return;
    pila[pila.length - 1].hijos.push(
      <Fragment key={clave++}>{crudo}</Fragment>
    );
  };

  TOKEN.lastIndex = 0;
  let encontrado;
  while ((encontrado = TOKEN.exec(texto)) !== null) {
    const [completo, cierre, etiqueta, url] = encontrado;
    agregarTexto(texto.slice(indice, encontrado.index));
    indice = encontrado.index + completo.length;

    if (!cierre) {
      pila.push({ etiqueta, url: url || null, hijos: [] });
      continue;
    }

    // Cierre: solo vale si es el que está abierto arriba de la pila.
    const abierta = pila[pila.length - 1];
    if (pila.length === 1 || abierta.etiqueta !== etiqueta) {
      agregarTexto(completo);
      continue;
    }

    pila.pop();
    const destino = pila[pila.length - 1].hijos;
    if (etiqueta === "url") {
      destino.push(
        <a key={clave++} href={abierta.url} target="_blank" rel="noreferrer">
          {abierta.hijos}
        </a>
      );
    } else {
      destino.push(ETIQUETAS[etiqueta](abierta.hijos, clave++));
    }
  }
  agregarTexto(texto.slice(indice));

  // Etiquetas sin cerrar: se vuelcan sus hijos al nivel de arriba para no
  // perder el texto.
  while (pila.length > 1) {
    const suelta = pila.pop();
    pila[pila.length - 1].hijos.push(
      <Fragment key={clave++}>{suelta.hijos}</Fragment>
    );
  }

  return pila[0].hijos;
}

/**
 * Props:
 *   texto  el contenido con etiquetas
 */
export default function TextoConFormato({ texto }) {
  if (!texto || !texto.trim()) return null;

  // Una línea en blanco separa párrafos; los saltos sueltos se respetan dentro
  // del párrafo, que es como se escribe en un textarea.
  const parrafos = texto
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <>
      {parrafos.map((parrafo, i) => (
        <p key={i} css={{ whiteSpace: "pre-line" }}>
          {nodosDeParrafo(parrafo.replace(SALTO_TRAS_BLOQUE, "$1"))}
        </p>
      ))}
    </>
  );
}
